import { useContext, useCallback, useRef } from 'react';
import { BigJunctureStation } from './BigJunctureStation';
import { ZoneContext } from 'context/ZoneContext';
import { BigJunctureStationWrapper, CollapseIcon } from './BigJuncture.styled';
import { TimelineWrapper } from '../../Station.styled';
import { StationStatus } from 'pages/Map/types';
import colors from 'theme/colors';
import { Description } from '../Description';
import BigJunctureSubStation from './BigJunctureSubStation';
import { getSectionColor } from '../Description/utils';
import { useExpandedStationOutsideClickAlerter } from '../../utils';
import useMapSearch from 'hooks/search/useMapSearch';

export const BigJunctureExpanded = ({ station }: { station: BigJunctureStation }) => {
    const {
        setSelectedStation,
        activeStation,
        paths,
        selectedPath,
        setExpandedStation,
        sections,
        mapState,
        expandedDescriptionStation,
        setExpandedDescriptionStation,
        selectedStation
    } = useContext(ZoneContext);
    const { getSearchColor } = useMapSearch();
    const stations = station.getStations();
    const stationStatus = station.getStationStatus(activeStation, paths, selectedStation);
    const stationColor = getSearchColor(station.id) || station.getStationColor(stationStatus);
    const timeline = station.getTimeline();
    const isDescriptionExpanded = expandedDescriptionStation?.id === station.id;
    const defaultZIndex = station.getDefaultZIndex(activeStation);
    const stationZIndex = isDescriptionExpanded ? station.getHoveredZIndex(defaultZIndex) : defaultZIndex;

    const onDescriptionExpand = useCallback(() => {
        setExpandedDescriptionStation(station);
    }, []);

    const onDescriptionCollapse = useCallback(() => {
        setExpandedDescriptionStation(undefined);
    }, []);

    const onStationClick = useCallback(() => {
        setSelectedStation(station);
    }, [station, setSelectedStation]);

    const onCollapse = useCallback(() => {
        setExpandedStation(undefined);
    }, [setExpandedStation]);

    const selectedPathIntersectionDirection = selectedPath ? station.getStationInterasectionDirectionForSelectedPath(selectedPath) : null;
    const sectionIndex = sections.findIndex(({ id }) => id === station.sectionID);
    const style = {
        left: station.getAbsoluteXValue(),
        top: station.getAbsoluteYValue(),
        backgroundColor: getSectionColor(sectionIndex),
        zIndex: stationZIndex,
        border: `0.3rem solid ${stationColor}`
    };
    const ref = useRef(null);
    const descriptionId = `${station.id}-description`;
    useExpandedStationOutsideClickAlerter(ref, onCollapse, mapState, {
        exceptionEleIds: ['path-selection-modal', descriptionId],
        exceptionEleClass: 'station-expand-trigger-element'
    });

    return (
        <BigJunctureStationWrapper
            ref={ref}
            id={station.id}
            style={style}
            selectedPathIntersectionDirection={selectedPathIntersectionDirection}
        >
            {stations.map(childStation => (
                <BigJunctureSubStation key={childStation.getId()} station={childStation} isBigJunctureExpanded={true} />
            ))}
            <Description
                id={descriptionId}
                onDescriptionExpand={onDescriptionExpand}
                onDescriptionCollapse={onDescriptionCollapse}
                station={station}
                isActiveStation={stationStatus === StationStatus.ACTIVE_STATION}
                onClick={onStationClick}
            />
            <CollapseIcon
                data-testid="big-juncture-collapse"
                size="XS"
                variant="solid"
                iconColor={colors.white}
                bgColor={colors.darkBlue}
                onClick={onCollapse}
            />
            {timeline ? <TimelineWrapper textVariant="body3">{timeline}</TimelineWrapper> : null}
        </BigJunctureStationWrapper>
    );
};
